import React from 'react';
import SEO from '../../components/SEO/SEO';

import { Link } from 'react-router-dom';
import './PaidAdsMain.css';

const PaidAdsMain = () => {
  const adServices = [
    {
      id: 'meta',
      title: 'Meta Ads',
      subtitle: 'Facebook & Instagram',
      description: 'Run high-converting campaigns on the world\'s largest social platforms with advanced targeting and creative testing.',
      icon: '📱',
      features: [
        'Creative Testing & A/B Optimization',
        'Audience Segmentation & Lookalikes',
        'Dynamic Product Ads & Retargeting',
        'Conversion Tracking & Attribution'
      ],
      link: '/services/paid-ads/meta'
    },
    {
      id: 'google',
      title: 'Google Ads',
      subtitle: 'Search, Display & Shopping',
      description: 'Capture high-intent customers with Google Search, Display, Shopping, and Performance Max campaigns.',
      icon: '🔍',
      features: [
        'Search Ads with Intent-Based Targeting',
        'Display & YouTube Video Campaigns',
        'Shopping Ads for E-Commerce',
        'Performance Max for Full-Funnel Growth'
      ],
      link: '/services/paid-ads/google'
    },
    {
      id: 'linkedin',
      title: 'LinkedIn Ads',
      subtitle: 'B2B Lead Generation',
      description: 'Reach decision-makers and generate qualified B2B leads with LinkedIn\'s professional targeting capabilities.',
      icon: '💼',
      features: [
        'Sponsored Content & InMail Campaigns',
        'Precise B2B Targeting by Role & Company',
        'Lead Gen Forms for Direct Capture',
        'Account-Based Marketing (ABM)'
      ],
      link: '/services/paid-ads/linkedin'
    },
    {
      id: 'tiktok',
      title: 'TikTok Ads',
      subtitle: 'Viral Video Marketing',
      description: 'Leverage short-form video storytelling and trend-based creatives to reach younger, highly engaged audiences.',
      icon: '🎬',
      features: [
        'Trend-Based Ad Creatives',
        'Short-Form Video Storytelling',
        'Interest & Behavior Targeting',
        'Spark Ads & Influencer Collaborations'
      ],
      link: '/services/paid-ads/tiktok'
    }
  ];

  return (
    <div className="paid-ads-page">
            <SEO 
                title="Paid Ads Main Services | Digi Network" 
                description="Learn more about our Paid Ads Main services to grow your business online with Digi Network." 
                canonical="/paidads/paidadsmain" 
            />

      <section className="paid-ads-hero">
        <div className="paid-ads-hero-bg"></div>
        <div className="container">
          <div className="paid-ads-hero-content">
            <div className="label">PAID ADVERTISING</div>
            <h1>High-ROI Ad Campaigns That Convert</h1>
            <p className="paid-ads-hero-description">
              Run full-funnel ad strategies with real-time optimization across Meta, Google, LinkedIn, and TikTok.
              Digi Network manages performance campaigns that turn clicks into customers and maximize every ad dollar.
            </p>
            <div className="paid-ads-hero-cta">
              <Link to="/contact" className="btn btn-primary">
                Get a Free Ad Strategy Session
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
              <Link to="/case-studies" className="btn btn-outline">View Results</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="paid-ads-stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">250%</div>
              <div className="stat-label">Average ROAS</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">$2M+</div>
              <div className="stat-label">Ad Spend Managed</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">45%</div>
              <div className="stat-label">Lower Cost Per Lead</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">98%</div>
              <div className="stat-label">Client Retention Rate</div>
            </div>
          </div>
        </div>
      </section>

      <section className="ad-services-grid-section">
        <div className="container">
          <div className="section-header">
            <div className="label">ADVERTISING PLATFORMS</div>
            <h2>Multi-Platform Ad Management</h2>
            <p>We manage campaigns across the platforms that matter most to your business.</p>
          </div>

          <div className="ad-services-grid">
            {adServices.map((service) => (
              <div key={service.id} className="ad-service-card">
                <div className="ad-service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <div className="ad-service-subtitle">{service.subtitle}</div>
                <p className="ad-service-description">{service.description}</p>

                <ul className="ad-service-features">
                  {service.features.map((feature, index) => (
                    <li key={index}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="10" fill="#0037FC" opacity="0.1"/>
                        <path d="M6 10L9 13L14 7" stroke="#0037FC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                <Link to={service.link} className="btn btn-dark">
                  Learn More
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="paid-ads-process">
        <div className="container">
          <div className="section-header">
            <div className="label">OUR APPROACH</div>
            <h2>Full-Funnel Ad Strategy</h2>
          </div>

          <div className="process-grid">
            <div className="process-step">
              <div className="process-number">01</div>
              <h3>Audit & Research</h3>
              <p>Analyze your market, competitors, and customer journey to identify opportunities.</p>
            </div>

            <div className="process-step">
              <div className="process-number">02</div>
              <h3>Campaign Setup</h3>
              <p>Build campaigns with precise targeting, compelling creatives, and conversion tracking.</p>
            </div>

            <div className="process-step">
              <div className="process-number">03</div>
              <h3>Launch & Test</h3>
              <p>Launch campaigns with A/B testing on creatives, audiences, and ad copy.</p>
            </div>

            <div className="process-step">
              <div className="process-number">04</div>
              <h3>Optimize & Scale</h3>
              <p>Continuously refine based on data, then scale winning campaigns for maximum ROI.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="paid-ads-cta">
        <div className="container">
          <div className="paid-ads-cta-content">
            <h2>Ready to Scale Your Ad Campaigns?</h2>
            <p>Get a free ad strategy session and discover how we can maximize your ROAS.</p>
            <Link to="/contact" className="btn btn-primary">
              Book Your Free Strategy Session
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PaidAdsMain;
